import React, { useContext, useEffect } from 'react';
import { Nav } from '@fluentui/react';
import { 
  ChatRegular, ChatFilled,
  SettingsRegular, SettingsFilled,
  MoneyRegular, MoneyFilled,
  CloudRegular, CloudFilled,
  HistoryRegular, HistoryFilled,
  QuestionCircleRegular, QuestionCircleFilled,
  DismissRegular, 
  PlugConnectedRegular, PlugConnectedFilled 
} from '@fluentui/react-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './LeftNavBar.module.css';
import { AppStateContext } from '../../state/AppProvider';
import { UserProfile } from '../Auth';
import { useAdminAccess } from '../Auth/AuthProvider';
import { buildMediaUrl } from '../../config/apiConfig';

interface LeftNavBarProps {
  selectedKey: string;
  onSelect: (key: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const navItems = [
  {
    key: 'chat',
    name: 'Chat',
    icon: <ChatRegular style={{ fontSize: 22 }} />,
    iconFilled: <ChatFilled style={{ fontSize: 22 }} />,
  },
  {
    key: 'history',
    name: 'History',
    icon: <HistoryRegular style={{ fontSize: 22 }} />,
    iconFilled: <HistoryFilled style={{ fontSize: 22 }} />,
    hidden: (ui?: any) => !(ui?.show_chat_history_button),
  },
  {
    key: 'help',
    name: 'Help',
    icon: <QuestionCircleRegular style={{ fontSize: 22 }} />,
    iconFilled: <QuestionCircleFilled style={{ fontSize: 22 }} />,
  },
  {
    key: 'admin',
    name: 'Admin',
    icon: <SettingsRegular style={{ fontSize: 22 }} />,
    iconFilled: <SettingsFilled style={{ fontSize: 22 }} />,
    adminOnly: true,
  },
  {
    key: 'cost',
    name: 'Costs',
    icon: <MoneyRegular style={{ fontSize: 22 }} />,
    iconFilled: <MoneyFilled style={{ fontSize: 22 }} />,
  },
  {
    key: 'cloud',
    name: 'Cloud',
    icon: <CloudRegular style={{ fontSize: 22 }} />,
    iconFilled: <CloudFilled style={{ fontSize: 22 }} />,
    adminOnly: true,
  },
  {
    key: 'ai-connector-hub',
    name: 'Connectors', 
    icon: <PlugConnectedRegular style={{ fontSize: 22 }} />,
    iconFilled: <PlugConnectedFilled style={{ fontSize: 22 }} />,
    adminOnly: true,
  },
];

export const LeftNavBar: React.FC<LeftNavBarProps> = ({ selectedKey, onSelect, isOpen = false, onClose }) => {
  const navigate = useNavigate();
  const appStateContext = useContext(AppStateContext);
  const ui = appStateContext?.state.frontendSettings?.ui;
  const chatLogo = ui?.chat_logo || ui?.logo;
  const { isAdmin, isAdminLoading } = useAdminAccess();

  // Use global model state
  const dispatch = appStateContext?.dispatch;

  // Close mobile nav when route changes
  useEffect(() => {
    if (onClose) {
      const handleResize = () => {
        if (window.innerWidth > 768 && isOpen) {
          onClose();
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (onClose && isOpen) {
      onClose();
    }
  }, [selectedKey]);

  // Filter navigation items based on admin access and UI settings
  const filteredNavItems = navItems.filter(item => {
    // If admin status is still loading, hide admin-only items to prevent flickering
    if ((item as any).adminOnly && isAdminLoading) {
      return false;
    }
    
    // Check if item requires admin access
    if ((item as any).adminOnly && !isAdmin) {
      return false;
    }
    
    // Check if item is hidden by UI settings
    if (item.hidden && item.hidden(ui)) {
      return false;
    }
    
    return true;
  });

  const handleNavClick = (key: string) => {
    onSelect(key);
    
    // Close mobile nav on navigation
    if (onClose && window.innerWidth <= 768) {
      onClose();
    }
    
    // Handle navigation based on key
    if (key === 'help') {
      navigate('/help');
    } else if (key === 'chat') {
      navigate('/');
    } else if (key === 'admin') {
      navigate('/admin');
    } else if (key === 'cost') {
      navigate('/cost');
    } else if (key === 'cloud') {
      navigate('/cloud');
    } else if (key === 'ai-connector-hub') {
      navigate('/ai-connector-hub');
    } else if (key === 'history') {
      // Toggle chat history panel
      if (appStateContext?.dispatch) {
        appStateContext.dispatch({ type: 'TOGGLE_CHAT_HISTORY' });
      }
      // Make sure we're on the chat page
      navigate('/');
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      <div 
        className={`${styles.mobileOverlay} ${isOpen ? styles.open : ''}`}
        onClick={onClose}
      />
      
      <nav className={`${styles.leftNavBar} ${isOpen ? styles.open : ''}`}>
        {/* Mobile close button */}
        {isOpen && (
          <button
            className={styles.mobileCloseButton}
            onClick={onClose}
            aria-label="Close navigation"
          >
            <DismissRegular style={{ fontSize: 20 }} />
          </button>
        )}
        
        {/* User Profile Section */}
        <div className={styles.userProfileSection}>
          <UserProfile isMinimized={true} />
        </div>
      
      {/* Logo only shown on mobile */}
      {isOpen && window.innerWidth <= 768 && chatLogo && (
        <div className={styles.logoContainer}>
          <img src={buildMediaUrl(chatLogo)} alt="Logo" className={styles.logoImg} />
        </div>
      )}
      <ul style={{ flex: 1 }}>
        {filteredNavItems.map(item => {
          const isSelected = selectedKey === item.key;
          return (
            <li
              key={item.key}
              className={isSelected ? styles.selected : ''}
              onClick={() => handleNavClick(item.key)}
              tabIndex={0}
              aria-label={item.name}
            >
              <span className={styles.icon}>
                {isSelected && (item as any).iconFilled ? (item as any).iconFilled : item.icon}
              </span>
              <span className={styles.label}>{item.name}</span>
            </li>
          );
        })}
      </ul>
    </nav>
    </>
  );
};
